import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import {
  api,
  staffWelcomeMessage,
  ApiError,
  type MySubUser,
  type SupportActivityEntry,
  type Tournament,
  type TournamentStaffMember,
} from "../lib/api";
import { formatDateTime } from "../lib/dateFormat";
import FieldHint from "../components/FieldHint";
import TournamentBottomNav from "../components/TournamentBottomNav";
import GuideHelpButton from "../components/GuideHelpButton";

// Perfiles limitados que se pueden asignar desde aquí (los dueños y
// co-admins legacy de poder total no se administran en esta pantalla).
const PROFILE_OPTIONS: Array<{ value: string; label: string; hint: string }> = [
  {
    value: "coadmin",
    label: "Coadministrador",
    hint: "Coadmin: puede hacer todo lo que tú en los torneos asignados, menos borrarlos.",
  },
  {
    value: "capturista",
    label: "Capturista",
    hint: "Solo programar partidos y capturar resultados. No puede tocar jugadores, draw ni cobros.",
  },
  {
    value: "finanzas",
    label: "Finanzas",
    hint: "Solo inscripciones y pagos: registrar pagos, validar comprobantes y cobrar.",
  },
  {
    value: "arbitro",
    label: "Árbitro",
    hint: "Solo ve y captura los partidos que le asignes en «Árbitros».",
  },
];

interface NewSubUserForm {
  full_name: string;
  email: string;
  password: string;
  phone: string;
}

// Qué pantalla tocó Soporte Torneos123 (bitácora del Equipo), en palabras.
const SUPPORT_AREAS: Array<[RegExp, string]> = [
  [/\/impersonation-token/, "Ver como jugador"],
  [/\/duplicate/, "Duplicó el torneo"],
  [/\/registrations/, "Inscripciones"],
  [/\/players|\/teams|\/participants/, "Jugadores y parejas"],
  [/\/divisions/, "Categorías"],
  [/\/schedule|\/matches|\/club-calendar/, "Calendario y partidos"],
  [/\/result-reports/, "Resultados reportados"],
  [/\/groups|\/rounds|\/jornada-dates|\/draw/, "Grupos, jornadas y cuadro"],
  [/\/standings|\/ranking/, "Posiciones y ranking"],
  [/\/communications/, "Avisos"],
  [/\/rules/, "Reglamento"],
  [/\/referees/, "Árbitros"],
  [/\/close|\/reopen|\/archive/, "Cierre del torneo"],
  [/\/settings|\/format-config|\/venues|\/images|\/logo|\/banner|\/call-pdf|\/whatsapp-groups|\/setup/, "Configuración"],
];

function describeSupportChange(entry: SupportActivityEntry): string {
  const path = entry.path ?? "";
  const rest = path.replace(/^.*\/admin\/tournaments\/\d+/, "");
  if (/\/admin\/tournaments\/?$/.test(path)) return "Creó el torneo";
  if (rest === "" || rest === "/") return "Datos del torneo";
  const found = SUPPORT_AREAS.find(([pattern]) => pattern.test(rest));
  return found ? found[1] : "Otro cambio";
}

const EMPTY_NEW_USER: NewSubUserForm = {
  full_name: "",
  email: "",
  password: "",
  phone: "",
};

export default function AdminTournamentTeamPage() {
  const { id } = useParams<{ id: string }>();
  const tournamentId = id ? Number(id) : NaN;
  const { askConfirm } = useAbortableConfirm([tournamentId]);
  const { token } = useAuth();

  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [team, setTeam] = useState<TournamentStaffMember[]>([]);
  const [myRole, setMyRole] = useState<string | null>(null);
  const [supportActivity, setSupportActivity] = useState<SupportActivityEntry[]>([]);
  const [subUsers, setSubUsers] = useState<MySubUser[]>([]);
  const [subUsersUsed, setSubUsersUsed] = useState(0);
  const [subUsersMax, setSubUsersMax] = useState(10);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyUserId, setBusyUserId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [mode, setMode] = useState<"new" | "existing">("new");
  const [profile, setProfile] = useState<string>("capturista");
  const [newUser, setNewUser] = useState<NewSubUserForm>(EMPTY_NEW_USER);
  const [existingUserId, setExistingUserId] = useState<string>("");

  // Solo el dueño (o superadmin, o co-admin legacy de poder total) gestiona
  // el equipo; los perfiles limitados ven la lista en modo lectura.
  const canManage = myRole === "owner" || myRole === "full" || myRole === "superadmin";

  const load = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId)) return;
    setLoading(true);
    setError(null);
    try {
      const [tournamentResponse, teamResponse] = await Promise.all([
        api.tournaments.get(token, tournamentId),
        api.tournaments.listTeam(token, tournamentId),
      ]);
      setTournament(tournamentResponse.data.tournament);
      setTeam(teamResponse.data.team);
      setMyRole(teamResponse.data.my_role);
      setSupportActivity(teamResponse.data.support_activity ?? []);
      // La lista de MIS sub-usuarios solo aplica a quien puede gestionar;
      // para un sub-usuario regresa vacía y no se muestra.
      try {
        const subsResponse = await api.tournaments.listMySubUsers(token);
        setSubUsers(subsResponse.data.sub_users);
        setSubUsersUsed(subsResponse.data.used);
        setSubUsersMax(subsResponse.data.max_sub_users);
      } catch {
        setSubUsers([]);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar el equipo del torneo.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setLoading(false);
    }
  }, [token, tournamentId]);

  useEffect(() => {
    void load();
  }, [load]);

  const teamUserIds = useMemo(
    () => new Set(team.map((member) => member.user_id)),
    [team],
  );
  const availableSubUsers = useMemo(
    () => subUsers.filter((sub) => !teamUserIds.has(sub.user_id)),
    [subUsers, teamUserIds],
  );
  const cupoLleno = subUsersUsed >= subUsersMax;

  useEffect(() => {
    // Con el cupo lleno, el modo por default pasa a reutilizar (crear va a
    // fallar con 409 de todos modos).
    if (cupoLleno && mode === "new" && availableSubUsers.length > 0) {
      setMode("existing");
    }
  }, [availableSubUsers.length, cupoLleno, mode]);

  const selectedProfile = PROFILE_OPTIONS.find((option) => option.value === profile);

  async function handleAdd(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token || saving) return;
    setError(null);
    setMessage(null);
    if (mode === "existing" && !existingUserId) {
      setError("Elige el sub-usuario que quieres asignar a este torneo.");
      return;
    }
    setSaving(true);
    try {
      const payload =
        mode === "new"
          ? {
              profile,
              new_user: {
                full_name: newUser.full_name.trim(),
                email: newUser.email.trim(),
                password: newUser.password,
                phone: newUser.phone.trim() || null,
              },
            }
          : { profile, user_id: Number(existingUserId) };
      const response = await api.tournaments.addTeamMember(token, tournamentId, payload);
      const member = response.data.member;
      setMessage(
        mode === "new"
          ? `Sub-usuario ${member.full_name} creado y agregado como ${member.profile_label}. ${staffWelcomeMessage(member.full_name, member.email, member.welcome_email)}`
          : `${member.full_name} agregado al equipo como ${member.profile_label}.`,
      );
      setNewUser(EMPTY_NEW_USER);
      setExistingUserId("");
      await load();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo agregar al equipo.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleChangeProfile(member: TournamentStaffMember, nextProfile: string) {
    if (!token || nextProfile === member.profile) return;
    setBusyUserId(member.user_id);
    setError(null);
    setMessage(null);
    try {
      await api.tournaments.updateTeamMember(token, tournamentId, member.user_id, {
        profile: nextProfile,
      });
      setMessage(`Perfil de ${member.full_name} actualizado.`);
      await load();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo cambiar el perfil.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setBusyUserId(null);
    }
  }

  async function handleRemove(member: TournamentStaffMember) {
    if (!token) return;
    if (
      !(await askConfirm(
        `¿Quitar a ${member.full_name} del equipo de este torneo? Su cuenta no se borra: podrás reasignarla a este u otros torneos cuando quieras.`, { acceptLabel: "Sí, quitar del equipo", tone: "danger" }))
    ) {
      return;
    }
    setBusyUserId(member.user_id);
    setError(null);
    setMessage(null);
    try {
      await api.tournaments.removeTeamMember(token, tournamentId, member.user_id);
      setMessage(`${member.full_name} ya no está en el equipo de este torneo.`);
      await load();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo quitar del equipo.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setBusyUserId(null);
    }
  }

  if (Number.isNaN(tournamentId)) {
    return (
      <section className="card">
        <p className="form-error">ID de torneo inválido.</p>
      </section>
    );
  }

  const isLimitedProfile = (value: string) =>
    value === "coadmin" || value === "capturista" || value === "finanzas" || value === "arbitro";

  return (
    <>
      <section className="card">
        <div className="card__header-row">
          <div>
            <h1 className="card__title">
              <span className="hint-label">
                Equipo del torneo
                {tournament ? ` · ${tournament.name}` : ""}
                <GuideHelpButton />
              </span>
            </h1>
            <p className="card__subtitle">
              Da acceso a tu equipo sin compartir tu contraseña: cada
              sub-usuario entra con su propia cuenta y solo puede hacer lo que
              su perfil permite en este torneo. Para darle acceso a varios de
              tus torneos o a todos, agrégalo en <Link to="/admin/team">Equipo</Link>;
              aquí puedes cambiar su perfil o quitarlo solo de este torneo.
            </p>
          </div>
        </div>
        {error && <p role="alert" className="form-error">{error}</p>}
        {message && <p className="form-message">{message}</p>}
        {!loading && !canManage && (
          <p className="muted">
            Tu perfil en este torneo no administra el equipo: solo el dueño del
            torneo puede agregar o quitar miembros.
          </p>
        )}
      </section>

      <section className="card">
        <div className="card__header-row">
          <div>
            <h2 className="card__title">Miembros</h2>
            <p className="card__subtitle">
              Quiénes tienen acceso a este torneo y con qué perfil.
            </p>
          </div>
        </div>
        {loading ? (
          <p className="muted">Cargando…</p>
        ) : team.length === 0 ? (
          <p className="muted">Todavía no hay miembros en el equipo.</p>
        ) : (
          <div className="responsive-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Correo</th>
                  <th>Perfil</th>
                  {canManage && <th>Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {team.map((member) => (
                  <tr key={member.user_id}>
                    <td>
                      <strong>{member.full_name}</strong>
                      {member.is_platform_support ? (
                        <span className="muted"> · de Torneos123</span>
                      ) : member.from_admin_team ? (
                        <span className="muted"> · de tu Equipo</span>
                      ) : member.managed_by_user_id !== null ? (
                        <span className="muted"> · sub-usuario</span>
                      ) : null}
                    </td>
                    <td>{member.email}</td>
                    <td>
                      {canManage && isLimitedProfile(member.profile) ? (
                        <select
                          value={member.profile}
                          onChange={(event) =>
                            void handleChangeProfile(member, event.target.value)
                          }
                          disabled={busyUserId === member.user_id}
                          aria-label={`Perfil de ${member.full_name}`}
                        >
                          {PROFILE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      ) : member.is_platform_support ? (
                        <span
                          className="status-pill support-badge"
                          title="Atiende a los organizadores. No toca dinero, no borra el torneo ni cambia su equipo."
                        >
                          {member.profile_label}
                        </span>
                      ) : (
                        <span className="status-pill status-pill--active">
                          {member.profile_label}
                        </span>
                      )}
                    </td>
                    {canManage && (
                      <td>
                        {isLimitedProfile(member.profile) ? (
                          <button
                            type="button"
                            className="btn btn--ghost btn--compact btn--tap44"
                            onClick={() => void handleRemove(member)}
                            disabled={busyUserId === member.user_id}
                          >
                            {busyUserId === member.user_id ? "Quitando…" : "Quitar"}
                          </button>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {!loading && supportActivity.length > 0 && (
        <section className="card support-activity" aria-labelledby="support-activity-title">
          <div className="card__header-row">
            <div>
              <h2 className="card__title" id="support-activity-title">Cambios de Soporte Torneos123</h2>
              <p className="card__subtitle">
                Cada cambio que hace Soporte Torneos123 en este torneo queda aquí con su nombre.
              </p>
            </div>
          </div>
          <ul className="support-activity__list">
            {supportActivity.map((entry, index) => (
              <li key={`${entry.at ?? ""}-${index}`} className="support-activity__item">
                <strong>{entry.user_name ?? "Soporte Torneos123"}</strong>
                <span>{describeSupportChange(entry)}</span>
                <span className="muted">{formatDateTime(entry.at)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {canManage && !loading && (
        <section className="card">
          <div className="card__header-row">
            <div>
              <h2 className="card__title">Agregar al equipo</h2>
              <p className="card__subtitle">
                Sub-usuarios creados: {subUsersUsed} de {subUsersMax}. El mismo
                sub-usuario puede ayudarte en varios torneos sin contar doble.
              </p>
            </div>
          </div>
          <form className="form" onSubmit={handleAdd}>
            <div className="form__actions" role="radiogroup" aria-label="Modo de alta">
              <label>
                <input
                  type="radio"
                  name="team-mode"
                  checked={mode === "new"}
                  onChange={() => setMode("new")}
                  disabled={saving}
                />{" "}
                Crear sub-usuario nuevo
              </label>
              <label>
                <input
                  type="radio"
                  name="team-mode"
                  checked={mode === "existing"}
                  onChange={() => setMode("existing")}
                  disabled={saving}
                />{" "}
                Reutilizar uno existente
              </label>
            </div>

            <label>
              <span className="hint-label">
                Perfil en este torneo
                {selectedProfile && (
                  <FieldHint>{selectedProfile.hint}</FieldHint>
                )}
              </span>
              <select
                value={profile}
                onChange={(event) => setProfile(event.target.value)}
                disabled={saving}
              >
                {PROFILE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            {mode === "new" ? (
              <>
                {cupoLleno && (
                  <p className="form-error" role="alert">
                    Ya usaste tus {subUsersMax} sub-usuarios. Reutiliza uno
                    existente o pide más cupo a Torneos123.
                  </p>
                )}
                <label>
                  Nombre completo
                  <input
                    type="text"
                    required
                    value={newUser.full_name}
                    onChange={(event) =>
                      setNewUser({ ...newUser, full_name: event.target.value })
                    }
                    disabled={saving || cupoLleno}
                    placeholder="Nombre del colaborador"
                  />
                </label>
                <label>
                  <span className="hint-label">
                    Correo
                    <FieldHint>
                      Con este correo iniciará sesión en Torneos123.
                    </FieldHint>
                  </span>
                  <input
                    type="email"
                    required
                    value={newUser.email}
                    onChange={(event) =>
                      setNewUser({ ...newUser, email: event.target.value })
                    }
                    disabled={saving || cupoLleno}
                    placeholder="colaborador@correo.com"
                  />
                </label>
                <label>
                  <span className="hint-label">
                    Contraseña inicial
                    <FieldHint>
                      Mínimo 6 caracteres. Compártesela por un medio seguro; podrá
                      cambiarla después.
                    </FieldHint>
                  </span>
                  <input
                    type="password"
                    required
                    minLength={6}
                    value={newUser.password}
                    onChange={(event) =>
                      setNewUser({ ...newUser, password: event.target.value })
                    }
                    disabled={saving || cupoLleno}
                    autoComplete="new-password"
                  />
                </label>
                <label>
                  Teléfono (opcional)
                  <input
                    type="tel"
                    value={newUser.phone}
                    onChange={(event) =>
                      setNewUser({ ...newUser, phone: event.target.value })
                    }
                    disabled={saving || cupoLleno}
                    placeholder="55 0000 0000"
                  />
                </label>
              </>
            ) : availableSubUsers.length === 0 ? (
              <p className="muted">
                {subUsers.length === 0
                  ? "Todavía no tienes sub-usuarios: crea el primero con la opción de arriba."
                  : "Todos tus sub-usuarios ya están en el equipo de este torneo."}
              </p>
            ) : (
              <label>
                Sub-usuario
                <select
                  value={existingUserId}
                  onChange={(event) => setExistingUserId(event.target.value)}
                  disabled={saving}
                  required
                >
                  <option value="">Elige un sub-usuario…</option>
                  {availableSubUsers.map((sub) => (
                    <option key={sub.user_id} value={sub.user_id}>
                      {sub.full_name} · {sub.email}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <div className="form__actions">
              <button
                className="btn"
                type="submit"
                disabled={
                  saving ||
                  (mode === "new" && cupoLleno) ||
                  (mode === "existing" && availableSubUsers.length === 0)
                }
              >
                {saving ? "Agregando…" : "Agregar al equipo"}
              </button>
            </div>
          </form>
        </section>
      )}

      <TournamentBottomNav tournamentId={tournamentId} />
    </>
  );
}
